import React, { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useAuthActions } from "@convex-dev/auth/react";
import { useConvexAuth } from "convex/react";
import { Alert, Button, InputWithLabel } from "../../../components/atoms/atoms.jsx";
import { useUserStore } from "../Store/userStore.js";
import { resolveDefaultTenantRoute } from "../../../utils/tenantUtils.js";

const SignInForm = ({ title, buttonName }) => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [localError, setLocalError] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const navigate = useNavigate();
  const location = useLocation();
  const { signIn } = useAuthActions();
  const { isAuthenticated } = useConvexAuth();
  const { showPassword, togglePasswordVisibility, mapAuthErrorToMessage } = useUserStore();

  // Auto-redirect when authenticated
  useEffect(() => {
    if (!isAuthenticated) return;
    const next = new URLSearchParams(location.search).get("next");
    if (next) {
      navigate(next, { replace: true });
      return;
    }
    resolveDefaultTenantRoute("home")
      .then((route) => navigate(route, { replace: true }))
      .catch(() => navigate("/"));
  }, [isAuthenticated, location.search, navigate]);

  // Auto-clear errors after 5 seconds
  useEffect(() => {
    if (!localError) return;
    const timer = setTimeout(() => setLocalError(null), 5000);
    return () => clearTimeout(timer);
  }, [localError]);

  const onLogin = async (e) => {
    e.preventDefault();
    if (!email) { setLocalError("Please enter your email address."); return; }
    if (!password) { setLocalError("Please enter your password."); return; }
    setIsSubmitting(true);
    try {
      await signIn("password", { flow: "signIn", email, password });
    } catch (err) {
      setLocalError(mapAuthErrorToMessage(err));
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="max-w-xl my-auto mx-auto lg:container">
      <h1 className="text-2xl text-primary font-bold mb-5">{title || "Sign in"}</h1>

      {localError && <Alert variant="destructive">{localError}</Alert>}

      <form onSubmit={onLogin}>
        <div className="mb-5">
          <InputWithLabel
            inputId="email"
            labelName="Your email"
            inputType="email"
            inputPlaceholder="----@-----.com"
            inputRequired
            value={email}
            onChange={(e) => setEmail(e.target.value)}
          />
        </div>

        <div className="mb-5">
          <InputWithLabel
            inputId="password"
            labelName="Your password"
            inputType={showPassword ? "text" : "password"}
            inputPlaceholder="**********"
            inputRequired
            value={password}
            onChange={(e) => setPassword(e.target.value)}
          />
          <div
            className="font-medium py-2 text-sm cursor-pointer"
            onClick={togglePasswordVisibility}
          >
            {showPassword ? "Hide" : "Show"} password
          </div>
        </div>

        <div
          className="inline-flex items-center text-sm hover:text-blue-900 cursor-pointer mb-5"
          onClick={() => navigate("/forgot-password")}
        >
          Forgot password?
        </div>

        <Button
          className="w-full"
          name={isSubmitting ? "Signing in…" : (buttonName || "Sign in")}
          disabled={isSubmitting}
        />
      </form>

      <p className="mt-5 text-center text-sm text-gray-500">
        Don&apos;t have an account?{" "}
        <button
          type="button"
          className="font-semibold text-primary hover:underline"
          onClick={() => navigate("/sign-up")}
        >
          Sign up for free
        </button>
      </p>

    </div>
  );
};

export default SignInForm;
